import { useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  formatarMinutos,
  formatarValor,
  lerDuracao,
  lerValor,
  nomeDaOrganizacao,
  useAlterarContratoDeHoras,
  useContratosDeHoras,
  useCriarContratoDeHoras,
  useExtratoDoContrato,
  useOrganizacoesDoAtendimento,
  type ContratoDeHoras,
} from "../../nucleo/horas";
import {
  Aviso,
  Botao,
  Campo,
  Cartao,
  Cabecalho,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../ui";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

function data(dia: string): string {
  const [ano, mes, diaDoMes] = dia.split("-");
  return `${diaDoMes}/${mes}/${ano}`;
}

function situacao(contrato: ContratoDeHoras): { rotulo: string; estado: "ok" | "neutro" | "atencao" } {
  if (!contrato.ativo) return { rotulo: "Inativo", estado: "neutro" };
  if (contrato.vigente) return { rotulo: "Vigente", estado: "ok" };
  return { rotulo: "Fora do período", estado: "atencao" };
}

function tomDoSaldo(contrato: ContratoDeHoras): "ok" | "atencao" | "erro" {
  if (contrato.saldo_minutos < 0) return "erro";
  if (contrato.alerta_saldo_minutos !== null && contrato.saldo_minutos <= contrato.alerta_saldo_minutos) {
    return "atencao";
  }
  return "ok";
}

function Extrato({ contratoId }: { contratoId: string }) {
  const extrato = useExtratoDoContrato(contratoId);
  if (extrato.isPending) return <p role="status">Carregando extrato…</p>;
  if (extrato.isError) return <Aviso tom="erro">{mensagemDoConsole(extrato.error)}</Aviso>;
  const { linhas, valor_consumido_centavos: valor } = extrato.data;
  return (
    <Cartao titulo={`Extrato de ${extrato.data.contrato.nome}`} className="mb-4" data-testid="extrato-do-contrato">
      <p className="cartao__texto mb-3">
        Valor consumido: <strong>{formatarValor(valor)}</strong>
      </p>
      {linhas.length === 0 ? (
        <p className="nota-rodape">Nenhum apontamento debitado deste contrato ainda.</p>
      ) : (
        <Tabela legenda="Apontamentos debitados do contrato">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Dia</CelulaCabecalho>
              <CelulaCabecalho>Chamado</CelulaCabecalho>
              <CelulaCabecalho>Quem</CelulaCabecalho>
              <CelulaCabecalho>Tempo</CelulaCabecalho>
              <CelulaCabecalho>Saldo depois</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {linhas.map((linha) => (
              <Linha key={linha.apontamento_id}>
                <Celula>{data(linha.dia)}</Celula>
                <Celula>
                  #{linha.ticket_numero} · {linha.ticket_assunto}
                </Celula>
                <Celula>{linha.agente_nome}</Celula>
                <Celula>{formatarMinutos(linha.minutos)}</Celula>
                <Celula>{formatarMinutos(linha.saldo_apos_minutos)}</Celula>
              </Linha>
            ))}
          </Corpo>
        </Tabela>
      )}
    </Cartao>
  );
}

function NovoContrato() {
  const organizacoes = useOrganizacoesDoAtendimento();
  const criar = useCriarContratoDeHoras();
  const voltarParaHome = useVoltarParaHome();
  const [organizacao, setOrganizacao] = useState("");
  const [nome, setNome] = useState("");
  const [horas, setHoras] = useState("");
  const [valor, setValor] = useState("");
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [alerta, setAlerta] = useState("");
  const [erros, setErros] = useState<Record<string, string>>({});

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const novos: Record<string, string> = {};
    const minutos = lerDuracao(horas);
    if (!organizacao) novos.organizacao = "Escolha a empresa.";
    if (!nome.trim()) novos.nome = "Dê um nome ao contrato.";
    if (minutos === null) novos.horas = "Informe as horas contratadas, como 40h ou 40.";
    if (!inicio) novos.inicio = "Informe o início.";
    if (!fim) novos.fim = "Informe o fim.";
    if (inicio && fim && fim < inicio) novos.fim = "O fim não pode ser anterior ao início.";
    const valorHora = valor.trim() ? lerValor(valor) : 0;
    if (valorHora === null) novos.valor = "Informe o valor em reais, como 150,00.";
    const limite = alerta.trim() ? lerDuracao(alerta) : null;
    if (alerta.trim() && limite === null) novos.alerta = "Informe o limite como 5h ou 300 (minutos).";
    setErros(novos);
    if (Object.keys(novos).length > 0 || minutos === null || valorHora === null) return;
    criar.mutate(
      {
        organizacao_id: organizacao,
        nome: nome.trim(),
        minutos_contratados: minutos,
        valor_hora_centavos: valorHora,
        inicio,
        fim,
        alerta_saldo_minutos: limite,
      },
      {
        onSuccess: () => {
          setNome("");
          setHoras("");
          setValor("");
          setInicio("");
          setFim("");
          setAlerta("");
          voltarParaHome("Contrato de horas criado.");
        },
      },
    );
  }

  return (
    <Cartao titulo="Novo contrato de horas" className="mb-4">
      <form className="grid gap-3" onSubmit={enviar} data-testid="novo-contrato-de-horas" noValidate>
        <div className="campo">
          <label className="campo__rotulo" htmlFor="contrato-empresa">Empresa</label>
          <select
            id="contrato-empresa"
            className="campo__controle"
            value={organizacao}
            onChange={(evento) => setOrganizacao(evento.target.value)}
          >
            <option value="">Escolha a empresa</option>
            {organizacoes.data?.map((item) => (
              <option value={item.id} key={item.id}>
                {nomeDaOrganizacao(item)}
              </option>
            ))}
          </select>
          {erros.organizacao ? <p className="campo__erro" role="alert">{erros.organizacao}</p> : null}
        </div>
        <Campo rotulo="Nome do contrato" value={nome} onChange={(e) => setNome(e.target.value)} erro={erros.nome} maxLength={120} />
        <div className="flex flex-wrap gap-3">
          <Campo rotulo="Horas contratadas" ajuda="Ex.: 40h ou 40" value={horas} onChange={(e) => setHoras(e.target.value)} erro={erros.horas} />
          <Campo rotulo="Valor da hora (R$)" value={valor} onChange={(e) => setValor(e.target.value)} erro={erros.valor} inputMode="decimal" />
          <Campo rotulo="Início" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} erro={erros.inicio} />
          <Campo rotulo="Fim" type="date" value={fim} onChange={(e) => setFim(e.target.value)} erro={erros.fim} />
          <Campo
            rotulo="Avisar quando restar"
            ajuda="Opcional. Ex.: 5h"
            value={alerta}
            onChange={(e) => setAlerta(e.target.value)}
            erro={erros.alerta}
          />
        </div>
        <div>
          <Botao type="submit" disabled={criar.isPending}>
            {criar.isPending ? "Criando…" : "Criar contrato"}
          </Botao>
        </div>
        {criar.isError ? <Aviso tom="erro">{mensagemDoConsole(criar.error)}</Aviso> : null}
      </form>
    </Cartao>
  );
}

/** Contratos de horas por empresa: saldo, período, valor/hora, alerta e extrato. */
export function PainelDeContratos() {
  const contratos = useContratosDeHoras();
  const organizacoes = useOrganizacoesDoAtendimento();
  const alterar = useAlterarContratoDeHoras();
  const [aberto, setAberto] = useState<string | null>(null);
  const nomes = new Map((organizacoes.data ?? []).map((item) => [item.id, nomeDaOrganizacao(item)]));

  return (
    <div data-testid="contratos-de-horas">
      <NovoContrato />
      {contratos.isPending ? (
        <p role="status">Carregando contratos…</p>
      ) : contratos.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(contratos.error)}</Aviso>
      ) : contratos.data.length === 0 ? (
        <Aviso tom="marca">Nenhum contrato de horas cadastrado.</Aviso>
      ) : (
        <Tabela legenda="Contratos de horas" data-testid="tabela-de-contratos">
          <Cabecalho>
            <Linha>
              <CelulaCabecalho>Empresa</CelulaCabecalho>
              <CelulaCabecalho>Contrato</CelulaCabecalho>
              <CelulaCabecalho>Período</CelulaCabecalho>
              <CelulaCabecalho>Contratadas</CelulaCabecalho>
              <CelulaCabecalho>Saldo</CelulaCabecalho>
              <CelulaCabecalho>Valor/hora</CelulaCabecalho>
              <CelulaCabecalho>Situação</CelulaCabecalho>
              <CelulaCabecalho>Ações</CelulaCabecalho>
            </Linha>
          </Cabecalho>
          <Corpo>
            {contratos.data.map((contrato) => {
              const estado = situacao(contrato);
              return (
                <Linha key={contrato.id} data-testid={`contrato-${contrato.nome}`}>
                  <Celula>{nomes.get(contrato.organizacao_id) ?? "—"}</Celula>
                  <Celula>{contrato.nome}</Celula>
                  <Celula>
                    {data(contrato.inicio)} a {data(contrato.fim)}
                  </Celula>
                  <Celula>{formatarMinutos(contrato.minutos_contratados)}</Celula>
                  <Celula>
                    <Chip estado={tomDoSaldo(contrato)}>{formatarMinutos(contrato.saldo_minutos)}</Chip>
                  </Celula>
                  <Celula>{formatarValor(contrato.valor_hora_centavos)}</Celula>
                  <Celula>
                    <Chip estado={estado.estado} semPonto>{estado.rotulo}</Chip>
                  </Celula>
                  <Celula>
                    <div className="flex gap-2">
                      <Botao
                        variante="fantasma"
                        tamanho="pequeno"
                        aria-label={`Extrato de ${contrato.nome}`}
                        onClick={() => setAberto(aberto === contrato.id ? null : contrato.id)}
                      >
                        {aberto === contrato.id ? "Fechar extrato" : "Extrato"}
                      </Botao>
                      <Botao
                        variante="fantasma"
                        tamanho="pequeno"
                        aria-label={`${contrato.ativo ? "Desativar" : "Ativar"} ${contrato.nome}`}
                        disabled={alterar.isPending}
                        onClick={() => alterar.mutate({ id: contrato.id, corpo: { ativo: !contrato.ativo } })}
                      >
                        {contrato.ativo ? "Desativar" : "Ativar"}
                      </Botao>
                    </div>
                  </Celula>
                </Linha>
              );
            })}
          </Corpo>
        </Tabela>
      )}
      {alterar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(alterar.error)}</Aviso> : null}
      {aberto ? <div className="mt-4"><Extrato contratoId={aberto} /></div> : null}
    </div>
  );
}
